import { Box, Grid } from "@mui/material";
import * as React from "react";
import * as eventService from "../../models/services/eventService";
import * as recordService from "../../models/services/recordService";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { ConfirmationDialogResult, DatabaseRecordType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../framework/appUtils";
import { SaveDataObj } from "../../framework/saveData";
import { Dialog } from "../framework/Dialog/Dialog";
import { DialogButtons } from "../framework/Dialog/DialogButtons";
import { DialogItemsNav } from "../framework/Dialog/DialogItemsNav";
import { TextFieldWithButtonGroup } from "../framework/Form/TextFieldWithButtonGroup";
import { FormField } from "../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../framework/Grid/GridRow";
import { SelectUnitDialog } from "../framework/Unit/SelectUnitDialog";
import { AvatarName } from "../framework/User/AvatarName";
import { IModulePartnerEditItem } from "source/models/modulePartner/modulePartnerEditItem";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";

interface IModulePartnerEditDialogProp {
    databaseRecordType: DatabaseRecordType;
    canRemovePartner: boolean;
    classes?: string;
    id: string;
    companyId: string;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onSaved: () => void;
    onClose: (refreshList: boolean) => void;
    onRemove: (id: string, code: string) => Promise<ApiSuccess>;
    onRefreshList: () => Promise<void>;
}

interface IModulePartnerEditDialogState {
    units: IIdTitleNode[];
    id: string;
    rowId: string;
    item: IModulePartnerEditItem;
    unitId: string;
    unitCode: string;
    showSelectUnitDialog: boolean;
}

export class ModulePartnerEditDialog extends React.Component<IModulePartnerEditDialogProp, IModulePartnerEditDialogState> {
    private orgStateHash: string = "";

    constructor(props: IModulePartnerEditDialogProp) {
        super(props);
        this.state = {
            units: [],
            id: "",
            rowId: "",
            item: null,
            unitId: "",
            unitCode: "",
            showSelectUnitDialog: false
        };
    }

    loadData = async (id: string) => {
        const props = this.props;
        const result = props.databaseRecordType === DatabaseRecordType.Event
            ? await AppUtils.callService(() => eventService.getEventPartnerEdit(props.companyId, id), false)
            : await AppUtils.callService(() => recordService.getRecordUserPartnerEdit(props.companyId, id), false);
        if (!result) return;
        const item = result.partner;
        const state = {
            ...this.state,
            units: result.units,
            id: item.id,
            rowId: item.rowId,
            item,
            unitId: item.unitId,
            unitCode: item.unitCode
        };
        this.setState(state);
        const saveData = this.getSaveDataFromState(this.props, state);
        this.orgStateHash = saveData.hash;
    };

    componentDidMount(): void {
        this.loadData(this.props.id);
    }

    async componentDidUpdate(prevProps: IModulePartnerEditDialogProp, prevState: IModulePartnerEditDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        if ((prevProps.companyId ?? "") === (props.companyId ?? "") && prevProps.id === props.id) return;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb()) return;
            props.onRefreshList();
        }
        this.loadData(this.props.id);
    }

    // #region Select Unit
    handleSelectUnit = () => {
        this.setState({
            showSelectUnitDialog: true
        });
    };

    handleSelectUnitDialogOk = async (node: IIdTitleNode) => {
        if (!node) return;
        this.setState({
            showSelectUnitDialog: false,
            unitId: node.id,
            unitCode: node.title
        });
    };

    handleSelectUnitDialogCancel = () => {
        this.setState({
            showSelectUnitDialog: false
        });
    };
    // #endregion Select Unit

    getSaveDataFromState = (props: IModulePartnerEditDialogProp, state: IModulePartnerEditDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("id", state.id);
        result.append("rowId", state.rowId);
        result.append("partnerCompanyId", state.item.partnerCompanyId);
        result.append("unitId", state.unitId);
        return result;
    };
    
    saveToDb = async (): Promise<SaveSuccess> => {
        const props = this.props;
        const state = this.state;
        const saveData = this.getSaveDataFromState(props, state);
        //Call server
        const result = props.databaseRecordType === DatabaseRecordType.Event
            ? await AppUtils.callService(() => eventService.saveEventPartnerEdit(saveData.obj))
            : await AppUtils.callService(() => recordService.saveRecordUserPartnerEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId };
        this.orgStateHash = (await this.getSaveDataFromState(props, { ...state, ...stateChange })).hash;
        this.setState(stateChange);
        return result;
    };

    handleSave = async (closeDialog: boolean) => {
        const result = await this.saveToDb();
        if (!result) return;
        if (!closeDialog) {
            this.loadData(result.id);
            this.props.onSaved();
            return;
        }
        this.props.onClose(true);
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemovePartnerParameter, state.item.partnerCompanyName), false) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await props.onRemove(state.id, state.item.partnerCompanyCode);
            if (!result) return;
            props.onClose(true);
        }
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose(false);
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(this.getSaveDataFromState(props, this.state).hash, this.orgStateHash,
            async () => !!(await this.saveToDb()),
            props.onRefreshList,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.item) return null;
        return (
            <Dialog
                id="modulePartnerEditDialog"
                maxWidth="sm"
                title={(props.databaseRecordType === DatabaseRecordType.Event ? Translations.EditEventPartner : Translations.EditRecordUserPartner) + ": " + state.item.partnerCompanyName}
                body={
                    <>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Partner} margin={0}>
                                    <AvatarName
                                        name={state.item.partnerCompanyName}
                                        picture={state.item.partnerCompanyLogo}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={12}>
                                <FormField title={Translations.Unit}>
                                    <TextFieldWithButtonGroup
                                        type="text"
                                        name="unitCode"
                                        title={Translations.Unit}
                                        disabled={true}
                                        value={state.unitCode}
                                        buttons={[
                                            { label: Translations.Select, onClick: this.handleSelectUnit }
                                        ]}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        {state.showSelectUnitDialog &&
                            <SelectUnitDialog
                                nodes={state.units}
                                title={Translations.SelectUnit}
                                firstColumnTitle={Translations.Unit}
                                selectedId={state.unitId}
                                onOk={this.handleSelectUnitDialogOk}
                                onCancel={this.handleSelectUnitDialogCancel}
                            />
                        }
                    </>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            modifyUser={state.item.modifyUser}
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                        />
                        <DialogButtons
                            isNew={false}
                            onCancel={this.handleCancel}
                            onRemove={props.canRemovePartner ? this.handleRemove : null }
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}
            />
        );
    }
}